import React, {
  CSSProperties,
  MutableRefObject,
  RefObject,
  useRef,
} from "react";
import { useAuth0, type Auth0ContextInterface } from '@equipmentshare/auth0-react'
import styled, { createGlobalStyle } from "styled-components";

import * as Button from "~/Button";
import * as Descriptions from "~/Descriptions";
import * as Document from "~/Document";
import cabinet from "~/Document/Cabinet.svg";
import { Component as EmptyComponent } from "~/Empty";
import * as Layout from "~/Layout";
import * as Loading from "~/Loading";
import * as Modal from "~/Modal";
import { Time } from "~/Time";
import * as Typography from "~/Typography";

import { Images } from "./images/Images";
import { PrintButton } from "./Print/PrintButton";

export * as Action from "./Action";
export * as Thumbnail from "./Thumbnail";

import { useAsset } from "~/../data/"

export type Props = {
  readonly document: Document.Active.Active;
  readonly show: boolean;
  readonly onHide: () => void;
};

export const Component: React.Fn<Props> = ({ document, show, onHide }) => {
  return (
    <Layout.Page.Component>
      {document ? (
        <DocumentDetailModal.Component
          document={document}
          show={show}
          onHide={onHide}
        />
      ) : (
        <Empty.Component />
      )}
    </Layout.Page.Component>
  );
};

export namespace Empty {
  type Props = {
    readonly title?: string;
    readonly noSubtitle?: boolean;
    readonly style?: CSSProperties;
  };
  export const Component: React.Fn<Props> = ({ title, noSubtitle, style }) => (
    <EmptyComponent
      title={title ?? "The document you selected is no longer available"}
      subtitle={noSubtitle ? undefined : "The Gremlins are at work again."}
      style={style ?? {}}
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      svg={{ src: cabinet }}
    />
  );
}
export namespace DocumentDetailModal {
  export const Component: React.Fn<Props> = ({ document, show, onHide }) => {
    const parentRef: MutableRefObject<HTMLElement | null> = useRef(null);

    if (!document) {
      return null;
    }

    return (
      <Layout.Flex.Component ref={parentRef}>
        <PrintMediaOverride.Component />
        <Modal.Component
          visible={show}
          size=""
          onCancel={onHide}
          Title={Document.driverName(document)}
          Footer={<Footer.Component document={document} onClose={onHide} />}
        >
          <Body.Component document={document} />
        </Modal.Component>
      </Layout.Flex.Component>
    );
  };

  export namespace Footer {
    type Props = {
      readonly document: Document.Document;
      readonly onClose: () => void;
    };

    export const Component: React.Fn<Props> = ({ document, onClose }) => {
      return (
        <Layout.Flex.Component row justify="flex-end" align="center">
          <Button.Component title="Close" onClick={onClose} />
          <PrintButton document={document} />
        </Layout.Flex.Component>
      );
    };
  }
}

export namespace Body {
  type Props = {
    readonly document: Document.Document;
  };
  export const Component: React.Fn<Props> = ({ document }) => {
    const documentImages =
    document.images && document.images.length > 0
      ? document.images.map((image) => image.imageUrl)
      : [];

    return (
      <Layout.Flex.Component
        row
        justify="center"
        align="flex-start"
        left
        right
        bottom
      >
        <Layout.Flex.Component row align="flex-start" top>
          <Images images={documentImages} width="18vw" />
        </Layout.Flex.Component>
        <Description.Component document={document} />
      </Layout.Flex.Component>
    );
  };
}

const DescriptionLoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 30px;
  min-width: 225px;
`;

export namespace Description {
  export type Props = {
    readonly document: Document.Document;
    readonly onlyUniqueProperties?: boolean;
  };

  export const Component: React.Fn<Props> = ({
    document,
    onlyUniqueProperties = false,
  }) => {

    // fetch asset details from asset id if it has one
    // const [ isLoading, setIsLoading ] = React.useState(true);
    const assetId = document?.properties?.find((property) => property.name === 'asset_id')?.value;
    const auth0: Auth0ContextInterface = useAuth0()
    const { data: asset, isFetching } = useAsset({
      assetId: assetId ?? '',
      auth0,
      enabled: !!assetId,
    })
    const assetName = asset?.custom_name

    if (isFetching) {
      return (
        <DescriptionLoadingWrapper>
          <Loading.Component />
        </DescriptionLoadingWrapper>
      )
    }

    const renderDescriptionProperty = ({
      key,
      label,
      value,
    }: { readonly key: string; readonly label: string; readonly value: string }) => (
      <Descriptions.Item.Component
          key={key}
          label={
            <Typography.Component muted>
              {propertyToLabel(label)}:
            </Typography.Component>
          }
        >
          <Typography.Component strong>
            {value}
          </Typography.Component>
        </Descriptions.Item.Component>
    )
    
    return (
      <Style>
        <Descriptions.Component bordered layout="horizontal" column={1}>
          {!onlyUniqueProperties && (
            <Descriptions.Item.Component
              label={<Typography.Component muted>Date:</Typography.Component>}
            >
              <Typography.Component strong>
                {Time.DateTime.toDateOnlyMMMDYYYYIgnoreTimezone(document)}
              </Typography.Component>
            </Descriptions.Item.Component>
          )}
          {!onlyUniqueProperties && (
            <Descriptions.Item.Component
              label={<Typography.Component muted>Type:</Typography.Component>}
            >
              <Typography.Component strong>{document.type}</Typography.Component>
            </Descriptions.Item.Component>
          )}
          { assetName && renderDescriptionProperty({ 
            key: assetName,
            label: 'Asset',
            value: assetName,
          }) }
          {document.properties
            ? document.properties.map((property, index) => renderDescriptionProperty({
                key: String(index),
                label: propertyToLabel(property.name),
                value: String(propertyValueToDisplayValue(property.name, property.value)),
            }))
            : null}
        </Descriptions.Component>
      </Style>
    )
  };

  const Style = styled.span`
    & > .es-descriptions-bordered .es-descriptions-view {
      border-color: transparent;
      padding-left: 1em;
      padding-right: 1em;
      min-width: 12vw;
    }

    .es-descriptions-bordered .es-descriptions-row {
      padding: 0.5em;
      border-color: transparent;
    }

    .es-descriptions-bordered .es-descriptions-item-label {
      background-color: transparent;
      border-color: transparent;
      padding: 0.5em;
    }

    .ant-descriptions-bordered .ant-descriptions-item-content {
      border-color: transparent;
      padding: 0.5em;
    }
  `;

  const propertyToLabel = (name: string): string =>
    name
      .replace("_", " ")
      .replace(
        /\w\S*/g,
        (value) => value.charAt(0).toUpperCase() + value.substr(1).toLowerCase()
      );

  const propertyValueToDisplayValue = (name: string, value: string | number) =>
    name.toLowerCase() === "cost" && value
      ? Number(value).toLocaleString("en-US", {
          style: "currency",
          currency: "USD",
        })
      : value;
}

export namespace Image {
  type ImageProps = {
    readonly imageUrl: string;
    readonly reference: RefObject<HTMLImageElement>;
    readonly height: number;
    readonly width: number;
    readonly onLoad: () => void;
    readonly onError: () => void;
    readonly onClick?: (event: React.MouseEvent<HTMLElement>) => void;
  };

  const ImageComponent = styled.img`
    display: inline-block;
    height: inherit;
    width: auto;
    cursor: zoom-in;
  `;

  export const Component: React.Fn<ImageProps> = ({
    imageUrl,
    reference,
    height,
    width,
    onError,
    onLoad,
    onClick,
  }) => (
    <ImageComponent
      src={imageUrl}
      ref={reference}
      style={{
        flexShrink: 0,
        minWidth: "100%",
        minHeight: "100%",
        height: `${height}px`,
        width: `${width}px`,
      }}
      loading="lazy"
      onLoad={onLoad}
      onError={onError}
      onClick={onClick}
    />
  );

  export type Dimension = {
    readonly height: number;
    readonly width: number;
  };

  export const resizeImage = (
    imageElement: HTMLImageElement,
    useNaturalSize: boolean,
    scaleHeightPercentage = 0.68,
    constrainToHeight: number | undefined = undefined
  ): Dimension => {
    const usableHeight: number = constrainToHeight
      ? constrainToHeight
      : useNaturalSize
      ? window.innerHeight
      : window.innerHeight * scaleHeightPercentage;

    const adjustedHeight: number = Math.min(
      usableHeight || imageElement.naturalHeight,
      imageElement.naturalHeight
    );

    const adjustedWidth: number =
      (adjustedHeight * imageElement.naturalWidth) / imageElement.naturalHeight;

    return {
      height: adjustedHeight,
      width: adjustedWidth,
    };
  };
}

export namespace NotFound {
  export type Props = {
    readonly size: number;
  };
  export const Component: React.Fn<Props> = ({ size }) => (
    <Layout.Flex.Component bordered style={{ width: size, height: size }}>
      <Typography.Component jumbo>Image not found</Typography.Component>
    </Layout.Flex.Component>
  );
}

namespace PrintMediaOverride {
  export const Component = createGlobalStyle`
    @media print {
      button, [type=button] {
        display: none !important;
      }
    }
  `;
}

export namespace Detail {}
